import { useEffect, useId, useRef, useState } from 'react';
import { Icon } from './Icon';
import { Modal } from './Modal';

interface Destination { id: string; label: string; control: HTMLElement }
/** Reads rendered navigation, retaining each app's role checks and unsaved-edit guards. */
export function CommandPalette({ sidebarId }: { sidebarId: string }) {
  const [open, setOpen] = useState(false), [query, setQuery] = useState(''), [active, setActive] = useState(0);
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const listId = useId(), input = useRef<HTMLInputElement>(null);
  const de = typeof document !== 'undefined' && document.documentElement.lang.startsWith('de');
  const label = de ? 'Im Arbeitsbereich suchen' : 'Search workspace';
  const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
  const words = normalize(query).trim().split(/\s+/).filter(Boolean);
  const matches = destinations.filter(d => words.every(word => normalize(d.label).includes(word)));
  function launch() {
    if (document.getElementById(sidebarId)?.closest('.fd-shell--nav-open')) return;
    const controls = Array.from(document.getElementById(sidebarId)?.querySelectorAll<HTMLElement>('.fd-navitem') ?? []);
    setDestinations(controls.filter(control => !control.hasAttribute('disabled') && control.getAttribute('aria-disabled') !== 'true')
      .map((control, i) => ({ id: `${listId}-${i}`, label: control.textContent?.trim() ?? '', control })).filter(d => d.label));
    setQuery(''); setActive(0); setOpen(true);
  }
  const launchRef = useRef(launch); launchRef.current = launch;
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return;
      // Keep rich-text link shortcuts and existing dialogs in control of their focus.
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('input,textarea,select,[contenteditable="true"],[role="dialog"]')) return;
      event.preventDefault(); launchRef.current();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => { if (open && matches[active]) document.getElementById(matches[active].id)?.scrollIntoView?.({ block: 'nearest' }); }, [open, active, query]);
  function choose(destination: Destination) {
    setOpen(false);
    if (destination.control.isConnected && document.getElementById(sidebarId)?.contains(destination.control) && !destination.control.hasAttribute('disabled')) destination.control.click();
  }
  return <>
    <button type="button" className="fd-command-trigger" aria-label={label} aria-haspopup="dialog" onClick={launch}>
      <Icon name="search" size={16} /><span>{de ? 'Seite suchen…' : 'Find a page…'}</span><kbd>⌘ K</kbd>
    </button>
    <Modal open={open} onClose={() => setOpen(false)} title={label} size="md">
      <div className="fd-command">
        <label className="fd-command__search"><Icon name="search" size={18} />
          <input ref={input} className="fd-input" role="combobox" aria-label={label} autoComplete="off" aria-autocomplete="list"
            aria-expanded="true" aria-controls={listId} aria-activedescendant={matches[active]?.id}
            placeholder={de ? 'Seiten und Bereiche finden…' : 'Find pages and sections…'} value={query}
            onChange={event => { setQuery(event.target.value); setActive(0); }}
            onKeyDown={event => {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault(); setActive(i => matches.length ? (i + (event.key === 'ArrowDown' ? 1 : -1) + matches.length) % matches.length : 0);
              } else if (event.key === 'Enter' && matches[active]) { event.preventDefault(); choose(matches[active]); }
            }} />
        </label>
        <div id={listId} role="listbox" aria-label={de ? 'Navigation' : 'Navigation'} className="fd-command__results">
          {matches.map((destination, index) => <button type="button" key={destination.id} id={destination.id}
            role="option" aria-selected={index === active} tabIndex={-1} className="fd-command__result"
            onMouseEnter={() => setActive(index)} onClick={() => choose(destination)}>
            <span>{destination.label}</span><Icon name="chevronRight" size={15} />
          </button>)}
          {!matches.length && <p className="fd-command__empty" role="status">{de ? 'Keine passenden Bereiche gefunden.' : 'No matching destinations.'}</p>}
        </div>
        <div className="fd-command__hint">{de ? '↑ ↓ Auswählen · Eingabe Öffnen · Esc Schließen' : '↑ ↓ Select · Enter Open · Esc Close'}</div>
      </div>
    </Modal>
  </>;
}
